import { type } from "arktype";
/**
 * The two review prompts and the per-user ledger behind them, stored in `user_app.review_prompts_raw`.
 *
 * - **The pulse** — our own sheet: "How are you liking Roo?" on a 0–10 slider scored like NPS, then ONE
 *   optional open question, shown whatever the score. First-party; the answers are ours to keep.
 * - **The store review** — the OS's native rating sheet, fired right after a value moment. The OS reports
 *   NOTHING back (not whether it displayed, not whether the user rated), so the ledger records only that
 *   we ASKED. Never add a field implying an outcome.
 *
 * ⚠ **A user whose latest pulse SCORE was a detractor's (0–6) is not asked for a store review — a TEMPORARY
 * gate** ({@link REVIEW_PROMPT_POLICY}`.storeReview.skipKnownDetractors`; ADR: review-prompt-gating holds the
 * exit condition). It is deliberately the NARROW gate: anyone who dismissed the pulse or never saw it is still
 * asked. They are most users, and rating volume is what protects the average while the listing has few
 * ratings. "Promoters only" was measured (it excludes ~122 users to keep out ~3) and rejected; don't widen this
 * into it without reading the ADR. The ledger holds both each score and every ask, so the gate's cost is one
 * query away.
 *
 * ⚠ **The two prompts are never within {@link REVIEW_PROMPT_POLICY}`.minDaysBetweenPrompts` of each
 * other, in either order.** Google's In-App Review rule forbids asking any question "before or while
 * presenting" the rating card; a gap of days (plus the client's same-session rule) is what keeps a
 * two-stage design on the right side of it. Loosening this gap is the change that turns a defensible
 * design into the prohibited one.
 *
 * Every function here is plain JS, so it behaves identically on mobile, where arktype is a no-op stub.
 * The server is authoritative: a client CLAIMS a prompt ({@link applyReviewPromptEvent}) and shows it only
 * if the claim is granted, so the caps hold across devices and against a stale client.
 */
// ---------------------------------------------------------------------------------------------------
// Policy. Every number a reviewer might want to move is here, by name.
export const REVIEW_PROMPT_POLICY = {
    pulse: {
        /** Days since account creation before the pulse may show. */
        minTenureDays: 3,
        /** Days between two pulses. */
        minDaysBetween: 180,
        lifetimeMax: 3,
    },
    storeReview: {
        /** Days since account creation. Median time to a first value moment is ~0.6 days, so without this
         * floor most users would be asked for finishing setup rather than for being served. */
        minTenureDays: 7,
        /** Days between two store-review asks. A guess — nothing in our data settles it. Apple's own cap
         * (3 per 365 days) applies underneath regardless. */
        minDaysBetween: 90,
        lifetimeMax: 3,
        /**
         * TEMPORARY — set to `false` once ADR: review-prompt-gating's exit condition is met. Skips a user whose most
         * recent pulse SCORE was a detractor's (0–6, {@link promoterCategory}). Passives, promoters, and anyone who
         * dismissed the pulse or never saw it are still asked. Lifting it is this flag plus the tests that pin it.
         */
        skipKnownDetractors: true,
    },
    /** Minimum days between a pulse and a store review, in EITHER order. See the header. */
    minDaysBetweenPrompts: 4,
    /** Longest follow-up answer the server accepts (refused, never truncated); the sheet clamps its inputs to it. */
    followUpMaxLength: 2000,
};
const DAY_MS = 24 * 60 * 60 * 1000;
// ---------------------------------------------------------------------------------------------------
// Value moments — what may fire a store-review ask.
/**
 * Every candidate moment, decided or rejected. `mayAskForReview` is REQUIRED with no default, so a new
 * moment does not compile until someone decides — the `suggestable` pattern from `VIDEO_REGISTRY`.
 * A moment qualifies only if the user is present, the gesture completed, and the outcome was positive.
 * Rejected candidates stay listed with their reason, so they aren't re-proposed on the same evidence.
 * Reach figures are live budgets, measured 2026-09-25.
 */
export const VALUE_MOMENTS = {
    inbox_zero: { mayAskForReview: true, why: "Sorted the last card in the card sorter. Reach ≤209, the primary." },
    income_split: { mayAskForReview: true, why: "An income split completed (server-confirmed). Reach ~156." },
    daily_cash: {
        mayAskForReview: true,
        // ⚠ Specified as "≥3 distinct days of daily cash" (reach ~154). The device never holds PROCESSED daily
        // cash — coreData carries pending cards only — so the streak isn't derivable without new persistence.
        // The 7-day store-review floor stands in for the habit; restoring the streak is a device-local set of
        // processed release dates, if the looser moment proves too eager.
        why: "Processed a daily-cash card. Past the 7-day floor, which stands in for a ≥3-day habit.",
    },
    payback_payment: {
        // Paused by the business (2026-10-06), not rejected on the evidence — still noted as a value moment, so
        // flipping this back is the whole re-enable.
        mayAskForReview: false,
        why: "Recorded a payback payment on a debt. Reach ~51. Paused for now by the business's choice.",
    },
    transfer_matched: { mayAskForReview: false, why: "Relief at tidying up, not value delivered. Reach ~118." },
    goal_met: {
        mayAskForReview: false,
        why: "Configured on 22 budgets and fired once, ever — too rare to carry anything. Revisit if it starts firing.",
    },
};
export function isValueMomentId(v) {
    return Object.prototype.hasOwnProperty.call(VALUE_MOMENTS, v);
}
// ---------------------------------------------------------------------------------------------------
// The ledger.
/**
 * The pulse is SCORED like NPS — whole numbers 0–10, the standard categories — so the answers translate
 * into a promoter score. The QUESTION is not the canonical NPS one ("How likely are you to recommend…"), by
 * the business's choice: for users days into Roo, "How are you liking Roo?" is the better question, and the
 * recommend question belongs to a later survey of well-acquainted users (out of scope here). Keep the SCALE
 * standard: a 1–10 scale would break the categories.
 */
export const PULSE_SCORE = { min: 0, max: 10 };
export function isPulseScore(v) {
    return typeof v === "number" && Number.isInteger(v) && v >= PULSE_SCORE.min && v <= PULSE_SCORE.max;
}
export function promoterCategory(score) {
    if (score >= 9)
        return "promoter";
    if (score >= 7)
        return "passive";
    return "detractor";
}
/**
 * The pulse's words, shared by the sheet and the server. `leadIn` is the line above the follow-up field, by
 * category; the same field follows for every score.
 */
export const PULSE_COPY = {
    title: "How's Roo doing?",
    question: "How are you liking Roo?",
    scaleLow: "Not at all",
    scaleHigh: "Loving it",
    /** The follow-up field's label. Deliberately short — the lead-in above it carries the question. */
    followUp: "Feedback",
    followUpPlaceholder: "What's working, what isn't, or what you wish Roo did",
    leadIn: {
        detractor: "Sorry Roo isn't there yet. We'd love to know what would make Roo better for you.",
        passive: "Thanks! We'd love to know what would make Roo better for you.",
        promoter: "That's wonderful to hear — thank you! We'd love to know if there's anything that would make Roo even better for you.",
    },
};
/**
 * The title of the `feedback` row a follow-up becomes — "How are you liking Roo? — 3/10". The field's own
 * label ("Feedback") says nothing in a triage list or a Slack page; the question plus the score says what the
 * text is answering, at a glance.
 */
export function pulseFeedbackTitle(score) {
    return `${PULSE_COPY.question} — ${score}/${PULSE_SCORE.max}`;
}
/**
 * Stored shape. Each array is bounded by its lifetime cap. `answer` is `null` while the sheet is open (or
 * if the app died with it open — the cap is spent at SHOW time, deliberately), `"scored"` with a `score`
 * (0–10) once answered, `"dismissed"` when closed without a score. Times are server-stamped epoch ms.
 *
 * Plain objects and literal unions only (no object `.or()`), so it is safe under the mobile Hermes stub.
 */
export const reviewPromptsSchema = type({
    pulse: type({
        shownAt: "number",
        answer: "'scored' | 'dismissed' | null",
        "score?": "number",
        "answeredAt?": "number",
    }).array(),
    storeReview: type({
        askedAt: "number",
        moment: "string",
    }).array(),
});
/** Fresh, unshared default (never prompted). */
export function createEmptyReviewPrompts() {
    return { pulse: [], storeReview: [] };
}
function isRecord(x) {
    return typeof x === "object" && x !== null && !Array.isArray(x);
}
function isFiniteNumber(x) {
    return typeof x === "number" && Number.isFinite(x);
}
function normalizePulseRecord(raw) {
    if (!isRecord(raw) || !isFiniteNumber(raw["shownAt"]))
        return null;
    const answer = raw["answer"];
    const shownAt = raw["shownAt"];
    const answeredAt = isFiniteNumber(raw["answeredAt"]) ? { answeredAt: raw["answeredAt"] } : {};
    // A "scored" record without a valid score can't be trusted as an answer: it reads as still open.
    if (answer === "scored" && isPulseScore(raw["score"]))
        return Object.assign({ shownAt, answer, score: raw["score"] }, answeredAt);
    if (answer === "dismissed")
        return Object.assign({ shownAt, answer }, answeredAt);
    return { shownAt, answer: null };
}
function normalizeStoreReviewRecord(raw) {
    if (!isRecord(raw) || !isFiniteNumber(raw["askedAt"]))
        return null;
    const moment = raw["moment"];
    if (typeof moment !== "string" || moment.length === 0)
        return null;
    return { askedAt: raw["askedAt"], moment };
}
/**
 * Interpret a raw `review_prompts_raw` value (NULL, partial, or malformed) as a complete ledger. The
 * read-time defaulter (NULL → never prompted, no backfill) and the server's write normalizer. Malformed
 * entries are dropped rather than repaired; records are kept oldest-first.
 */
export function normalizeReviewPrompts(raw) {
    if (!isRecord(raw))
        return createEmptyReviewPrompts();
    const pulse = (Array.isArray(raw["pulse"]) ? raw["pulse"] : [])
        .map(normalizePulseRecord)
        .filter((r) => r !== null)
        .sort((a, b) => a.shownAt - b.shownAt);
    const storeReview = (Array.isArray(raw["storeReview"]) ? raw["storeReview"] : [])
        .map(normalizeStoreReviewRecord)
        .filter((r) => r !== null)
        .sort((a, b) => a.askedAt - b.askedAt);
    return { pulse, storeReview };
}
// ---------------------------------------------------------------------------------------------------
// Audience.
/**
 * Staff, e2e and review accounts are never prompted — their answers would pollute the only first-party
 * satisfaction data we have. Matches `@roo.money` and any subdomain (`@e2e.roo.money`), which also covers
 * `tester@roo.money`, the App Store reviewer's account. Nothing else on the client identifies staff today.
 */
export function isStaffOrTestEmail(email) {
    return /@(?:[a-z0-9-]+\.)*roo\.money$/i.test(email.trim());
}
function daysBetween(fromMs, toMs) {
    return (toMs - fromMs) / DAY_MS;
}
function lastOf(items) {
    return items[items.length - 1];
}
/** May the pulse show now, as far as audience, tenure and the ledger are concerned? */
export function checkPulseLedger(p) {
    const policy = REVIEW_PROMPT_POLICY.pulse;
    if (isStaffOrTestEmail(p.email))
        return { eligible: false, reason: "STAFF_OR_TEST" };
    if (daysBetween(p.accountCreatedAtMs, p.nowMs) < policy.minTenureDays)
        return { eligible: false, reason: "TENURE" };
    if (p.ledger.pulse.length >= policy.lifetimeMax)
        return { eligible: false, reason: "LIFETIME_CAP" };
    const lastPulse = lastOf(p.ledger.pulse);
    if (lastPulse && daysBetween(lastPulse.shownAt, p.nowMs) < policy.minDaysBetween) {
        return { eligible: false, reason: "TOO_SOON_SINCE_LAST" };
    }
    const lastAsk = lastOf(p.ledger.storeReview);
    if (lastAsk && daysBetween(lastAsk.askedAt, p.nowMs) < REVIEW_PROMPT_POLICY.minDaysBetweenPrompts) {
        return { eligible: false, reason: "TOO_CLOSE_TO_OTHER_PROMPT" };
    }
    return { eligible: true };
}
/**
 * The score from the user's most recent ANSWERED pulse, or `null` if they have never scored one. A later
 * dismissal doesn't erase an earlier score: it is still the last thing they told us. ⚠ `0` is a real score —
 * compare the result to `null`, never test it for truthiness.
 */
export function latestPulseScore(ledger) {
    for (let i = ledger.pulse.length - 1; i >= 0; i--) {
        const record = ledger.pulse[i];
        if ((record === null || record === void 0 ? void 0 : record.answer) === "scored" && record.score !== undefined)
            return record.score;
    }
    return null;
}
/**
 * May the store review be asked now, after `moment`? The pulse matters in one way only: a known detractor
 * is not asked (temporary — see the header). A user who never saw the pulse, or dismissed it, is NOT blocked:
 * the pulse usually comes first because its floor is lower, but nothing requires it.
 */
export function checkStoreReviewLedger(p) {
    const policy = REVIEW_PROMPT_POLICY.storeReview;
    if (!VALUE_MOMENTS[p.moment].mayAskForReview)
        return { eligible: false, reason: "MOMENT_NOT_ALLOWED" };
    if (isStaffOrTestEmail(p.email))
        return { eligible: false, reason: "STAFF_OR_TEST" };
    if (daysBetween(p.accountCreatedAtMs, p.nowMs) < policy.minTenureDays)
        return { eligible: false, reason: "TENURE" };
    const lastScore = latestPulseScore(p.ledger);
    if (policy.skipKnownDetractors && lastScore !== null && promoterCategory(lastScore) === "detractor") {
        return { eligible: false, reason: "KNOWN_DETRACTOR" };
    }
    if (p.ledger.storeReview.length >= policy.lifetimeMax)
        return { eligible: false, reason: "LIFETIME_CAP" };
    const lastAsk = lastOf(p.ledger.storeReview);
    if (lastAsk && daysBetween(lastAsk.askedAt, p.nowMs) < policy.minDaysBetween) {
        return { eligible: false, reason: "TOO_SOON_SINCE_LAST" };
    }
    const lastPulse = lastOf(p.ledger.pulse);
    if (lastPulse && daysBetween(lastPulse.shownAt, p.nowMs) < REVIEW_PROMPT_POLICY.minDaysBetweenPrompts) {
        return { eligible: false, reason: "TOO_CLOSE_TO_OTHER_PROMPT" };
    }
    return { eligible: true };
}
/**
 * Apply one event to the ledger. Returns a NEW normalized ledger; never mutates `current`.
 *
 * - `claimPulse` / `claimStoreReview` run the matching eligibility check and append a record only when
 *   it passes. The client shows a prompt only on `granted: true`.
 * - `answerPulse` / `dismissPulse` close ONE pulse, and closing it twice is a no-op. The client names the
 *   pulse by its `shownAt` (the claim's server-stamped time), so a retry or a second close lands on the
 *   same record and comes back `duplicate` — one showing can never spend two lifetime slots, restart the
 *   window twice, or file its text twice. Without `shownAt` (an older client), the newest open pulse is
 *   closed. If no pulse is open at all, a closed record is appended — but only under the lifetime cap.
 */
export function applyReviewPromptEvent(current, p) {
    var _a, _b;
    const ledger = normalizeReviewPrompts(current);
    const check = { ledger, nowMs: p.nowMs, accountCreatedAtMs: p.accountCreatedAtMs, email: p.email };
    const event = p.event;
    switch (event.kind) {
        case "claimPulse": {
            const eligibility = checkPulseLedger(check);
            if (!eligibility.eligible)
                return { granted: false, reason: eligibility.reason, duplicate: false, next: ledger };
            return {
                granted: true,
                duplicate: false,
                next: Object.assign(Object.assign({}, ledger), { pulse: [...ledger.pulse, { shownAt: p.nowMs, answer: null }] }),
            };
        }
        case "claimStoreReview": {
            const eligibility = checkStoreReviewLedger(Object.assign(Object.assign({}, check), { moment: event.moment }));
            if (!eligibility.eligible)
                return { granted: false, reason: eligibility.reason, duplicate: false, next: ledger };
            return {
                granted: true,
                duplicate: false,
                next: Object.assign(Object.assign({}, ledger), { storeReview: [...ledger.storeReview, { askedAt: p.nowMs, moment: event.moment }] }),
            };
        }
        case "answerPulse":
        case "dismissPulse": {
            if (event.kind === "answerPulse" && !isPulseScore(event.score)) {
                return { granted: false, reason: "INVALID_SCORE", duplicate: false, next: ledger };
            }
            const outcome = event.kind === "answerPulse" ? { answer: "scored", score: event.score } : { answer: "dismissed" };
            const close = (index) => {
                var _a;
                return ({
                    granted: true,
                    duplicate: false,
                    closedPulseShownAt: (_a = ledger.pulse[index]) === null || _a === void 0 ? void 0 : _a.shownAt,
                    next: Object.assign(Object.assign({}, ledger), { pulse: ledger.pulse.map((r, i) => i === index ? Object.assign(Object.assign({ shownAt: r.shownAt }, outcome), { answeredAt: p.nowMs }) : r) }),
                });
            };
            const named = event.shownAt === undefined ? -1 : ledger.pulse.findIndex((r) => r.shownAt === event.shownAt);
            if (named >= 0) {
                // The pulse the client showed. Already closed → this is a retry or a second close: change nothing.
                if (((_a = ledger.pulse[named]) === null || _a === void 0 ? void 0 : _a.answer) !== null)
                    return { granted: true, duplicate: true, next: ledger };
                return close(named);
            }
            let openIndex = -1;
            for (let i = ledger.pulse.length - 1; i >= 0 && openIndex < 0; i--) {
                if (((_b = ledger.pulse[i]) === null || _b === void 0 ? void 0 : _b.answer) === null)
                    openIndex = i;
            }
            if (openIndex >= 0)
                return close(openIndex);
            if (ledger.pulse.length >= REVIEW_PROMPT_POLICY.pulse.lifetimeMax) {
                return { granted: false, reason: "NO_OPEN_PULSE", duplicate: false, next: ledger };
            }
            return {
                granted: true,
                duplicate: false,
                closedPulseShownAt: p.nowMs,
                next: Object.assign(Object.assign({}, ledger), { pulse: [...ledger.pulse, Object.assign(Object.assign({ shownAt: p.nowMs }, outcome), { answeredAt: p.nowMs })] }),
            };
        }
    }
}
//# sourceMappingURL=reviewPrompts.models.js.map